import { FC, FocusEvent, useId, useState } from 'react';
import { Card } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';

import { FinancialOverviewPanelProps } from '../../types';
import { useFinancialOverviewSectionDrag } from '../FinancialOverviewSectionDragContext/context';
import { FinancialOverviewSectionHeader } from '../FinancialOverviewSectionHeader';

export const FinancialOverviewPanel: FC<FinancialOverviewPanelProps> = ({
    actions,
    children,
    collapsed,
    collapsible = true,
    defaultCollapsed = false,
    description,
    icon,
    onCollapsedChange,
    style,
    title,
}) => {
    const { token } = useAntDesignTheme();
    const dragControl = useFinancialOverviewSectionDrag();
    const titleId = useId();
    const contentId = `${titleId}-content`;
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const [collapseControlFocused, setCollapseControlFocused] = useState(false);
    const [sectionHovered, setSectionHovered] = useState(false);
    const canCollapse = collapsible && Boolean(title);
    const resolvedCollapsed = canCollapse ? (collapsed ?? internalCollapsed) : false;
    const setCollapsed = (nextCollapsed: boolean) => {
        setCollapseControlFocused(false);
        setSectionHovered(false);

        if (collapsed === undefined) {
            setInternalCollapsed(nextCollapsed);
        }

        onCollapsedChange?.(nextCollapsed);
    };
    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setCollapseControlFocused(false);
        }
    };

    return (
        <Card
            aria-labelledby={title ? titleId : undefined}
            onBlurCapture={handleBlur}
            onFocusCapture={() => setCollapseControlFocused(true)}
            onMouseEnter={() => setSectionHovered(true)}
            onMouseLeave={() => setSectionHovered(false)}
            style={{
                borderColor: token.colorBorderSecondary,
                boxShadow: 'none',
                maxWidth: '100%',
                minWidth: 0,
                ...style,
            }}
            styles={{ body: { minWidth: 0, padding: '1.25rem' } }}
        >
            {title ? (
                <FinancialOverviewSectionHeader
                    actions={actions}
                    collapseControl={
                        canCollapse
                            ? {
                                  collapsed: resolvedCollapsed,
                                  contentId,
                                  label: typeof title === 'string' ? title : 'section',
                                  onChange: setCollapsed,
                                  visible: collapseControlFocused || sectionHovered || Boolean(dragControl?.visible),
                              }
                            : undefined
                    }
                    description={description}
                    dragControl={dragControl}
                    icon={icon}
                    style={{
                        marginBottom: resolvedCollapsed ? 0 : '1rem',
                        transition: `margin-bottom ${token.motionDurationMid} ${token.motionEaseInOut}`,
                    }}
                    title={title}
                    titleId={titleId}
                />
            ) : null}
            <div
                aria-hidden={resolvedCollapsed}
                id={contentId}
                style={{
                    display: 'grid',
                    gridTemplateRows: resolvedCollapsed ? '0fr' : '1fr',
                    opacity: resolvedCollapsed ? 0 : 1,
                    transition: `grid-template-rows ${token.motionDurationMid} ${token.motionEaseInOut}, opacity ${token.motionDurationMid} ${token.motionEaseInOut}, visibility 0s linear ${resolvedCollapsed ? token.motionDurationMid : '0s'}`,
                    visibility: resolvedCollapsed ? 'hidden' : 'visible',
                }}
            >
                <div style={{ minHeight: 0, overflow: 'hidden' }}>{children}</div>
            </div>
        </Card>
    );
};
